import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Building,
  CheckCircle,
  CreditCard,
  Edit,
  Eye,
  Filter,
  Globe,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./SuppliersList.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  if (company.currency && typeof company.currency === "object" && company.currency.code) {
    return company.currency.code;
  }
  if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
    return company.currency;
  }
  return company.currency_code || company.base_currency || "USD";
};

// ✅ Format any amount with any currency code/object
const formatSupplierCurrency = (amount, currencyInput) => {
  let currencyCode = "USD";
  if (typeof currencyInput === "string" && /^[A-Z]{3}$/.test(currencyInput)) {
    currencyCode = currencyInput;
  } else if (currencyInput && typeof currencyInput === "object" && currencyInput.code) {
    currencyCode = currencyInput.code;
  } else if (currencyInput && typeof currencyInput === "string") {
    currencyCode = currencyInput;
  }

  const num = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currencyCode} ${num.toFixed(2)}`;
  }
};

export default function SuppliersList() {
  const { currentUser, userType, company } = useAuth();
  const { getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const isSystemAdmin =
    userType === "system" && ["developer", "admin"].includes(currentUser?.role);

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [status, setStatus] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/supplier`, {
        params: {
          status,
          ...(isSystemAdmin && selectedCompany ? { company_id: selectedCompany } : {}),
          limit: 200,
        },
      });
      setSuppliers(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch suppliers");
    } finally {
      setLoading(false);
    }
  }, [isSystemAdmin, selectedCompany, status]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    if (!isSystemAdmin) return;
    let active = true;

    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await axios.get(`${url}/api/company`);
        if (active) setCompanies(response.data?.data || []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load companies");
      } finally {
        if (active) setLoadingCompanies(false);
      }
    };

    fetchCompanies();
    return () => {
      active = false;
    };
  }, [isSystemAdmin]);

  const filteredSuppliers = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) return suppliers;

    return suppliers.filter((supplier) =>
      [
        supplier.supplier_name,
        supplier.supplier_id,
        supplier.phone,
        supplier.email,
        supplier.company_id?.company_name,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(value)),
    );
  }, [searchTerm, suppliers]);

  // ✅ Stats – all amounts displayed in company currency
  const stats = useMemo(() => {
    return suppliers.reduce(
      (result, supplier) => {
        result.total += 1;
        if (supplier.status === "active") result.active += 1;
        if (supplier.email) result.withEmail += 1;

        const opening = Number(supplier.opening_balance || 0);
        if (supplier.opening_balance_side === "debit") result.advances += opening;
        else result.payables += opening;

        result.creditLimit += Number(supplier.credit_limit || 0);
        return result;
      },
      { total: 0, active: 0, withEmail: 0, payables: 0, advances: 0, creditLimit: 0 },
    );
  }, [suppliers]);

  const deactivateSupplier = async (supplier) => {
    if (
      !window.confirm(
        `Deactivate ${supplier.supplier_name}? It will no longer be available for new purchases.`,
      )
    ) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/supplier/${supplier._id}`);
      toast.success(response.data?.message || "Supplier deactivated");
      await fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to deactivate supplier");
    }
  };

  return (
    <main className="supplier-list-page">
      <header className="supplier-list-header">
        <div>
          <p className="supplier-list-eyebrow">Purchasing</p>
          <h1>Suppliers</h1>
          <p>
            {isSystemAdmin ? (
              <><Globe size={14} /> Supplier masters across companies</>
            ) : (
              <><Building size={14} /> {company?.company_name || "Your company"}</>
            )}
          </p>
        </div>

        <div className="supplier-list-header__actions">
          <button
            type="button"
            className="supplier-list-button supplier-list-button--secondary"
            onClick={() => setShowFilters((current) => !current)}
          >
            <Filter size={16} /> Filters
          </button>
          <button
            type="button"
            className="supplier-list-button supplier-list-button--primary"
            onClick={() => navigate("/AddSuppliers")}
          >
            <Plus size={16} /> Add supplier
          </button>
        </div>
      </header>

      <section className="supplier-list-stats" aria-label="Supplier statistics">
        <article><User size={20} /><div><strong>{stats.total}</strong><span>Total suppliers</span></div></article>
        <article><CheckCircle size={20} /><div><strong>{stats.active}</strong><span>Active</span></div></article>
        <article><Mail size={20} /><div><strong>{stats.withEmail}</strong><span>With email</span></div></article>
        <article>
          <span className="supplier-list-stat-symbol">{getCurrencySymbol(companyCurrency)}</span>
          <div>
            <strong>{formatSupplierCurrency(stats.payables, companyCurrency)}</strong>
            <span>Opening payables</span>
          </div>
        </article>
        <article>
          <span className="supplier-list-stat-symbol">{getCurrencySymbol(companyCurrency)}</span>
          <div>
            <strong>{formatSupplierCurrency(stats.advances, companyCurrency)}</strong>
            <span>Supplier advances</span>
          </div>
        </article>
        <article>
          <CreditCard size={20} />
          <div>
            <strong>{formatSupplierCurrency(stats.creditLimit, companyCurrency)}</strong>
            <span>Total credit limits</span>
          </div>
        </article>
      </section>

      {showFilters && (
        <section className="supplier-list-filters">
          {isSystemAdmin && (
            <label>
              Company
              <select
                value={selectedCompany}
                onChange={(event) => setSelectedCompany(event.target.value)}
                disabled={loadingCompanies}
              >
                <option value="">All companies</option>
                {companies.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.company_name}{item.company_code ? ` (${item.company_code})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All statuses</option>
            </select>
          </label>
        </section>
      )}

      <section className="supplier-list-workspace">
        <div className="supplier-list-toolbar">
          <div className="supplier-list-search">
            <Search size={17} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search supplier, ID, phone, email or company"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm("")} aria-label="Clear search">
                <X size={15} />
              </button>
            )}
          </div>

          <button
            type="button"
            className="supplier-list-refresh"
            onClick={fetchSuppliers}
            title="Refresh suppliers"
          >
            <RefreshCw size={17} />
          </button>
        </div>

        <div className="supplier-list-table-wrap">
          <table className="supplier-list-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Contact</th>
                {isSystemAdmin && <th>Company</th>}
                <th>Opening balance</th>
                <th>Credit limit</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isSystemAdmin ? 8 : 7}>
                    <div className="supplier-list-empty"><Loader2 className="supplier-list-spin" /> Loading suppliers…</div>
                  </td>
                </tr>
              ) : filteredSuppliers.length === 0 ? (
                <tr>
                  <td colSpan={isSystemAdmin ? 8 : 7}>
                    <div className="supplier-list-empty">No suppliers found.</div>
                  </td>
                </tr>
              ) : (
                filteredSuppliers.map((supplier) => {
                  const currency = companyCurrency;
                  const openingAmount = Number(supplier.opening_balance || 0);
                  const side = supplier.opening_balance_side === "debit" ? "DR" : "CR";
                  return (
                    <tr key={supplier._id}>
                      <td>
                        <div className="supplier-list-name-cell">
                          <strong>{supplier.supplier_name}</strong>
                          <span>SUP-{String(supplier.supplier_id || "").padStart(4, "0")}</span>
                        </div>
                      </td>
                      <td>
                        <div className="supplier-list-contact">
                          <span><Phone size={13} /> {supplier.phone || "—"}</span>
                          <span><Mail size={13} /> {supplier.email || "—"}</span>
                        </div>
                      </td>
                      {isSystemAdmin && (
                        <td>{supplier.company_id?.company_name || "—"}</td>
                      )}
                      <td className="supplier-list-money">
                        {formatSupplierCurrency(openingAmount, currency)} {openingAmount ? side : ""}
                      </td>
                      <td className="supplier-list-money">
                        {formatSupplierCurrency(supplier.credit_limit || 0, currency)}
                      </td>
                      <td>
                        <span className={`supplier-list-status supplier-list-status--${supplier.status}`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>{supplier.created_at ? new Date(supplier.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="supplier-list-actions">
                          <button type="button" onClick={() => setSelectedSupplier(supplier)} title="View supplier">
                            <Eye size={15} />
                          </button>
                          <button type="button" onClick={() => navigate(`/supplier/edit/${supplier._id}`)} title="Edit supplier">
                            <Edit size={15} />
                          </button>
                          <button
                            type="button"
                            className="supplier-list-danger"
                            onClick={() => deactivateSupplier(supplier)}
                            disabled={supplier.status === "inactive"}
                            title="Deactivate supplier"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="supplier-list-footer">
          Showing {filteredSuppliers.length} of {suppliers.length} suppliers
        </footer>
      </section>

      {selectedSupplier && (
        <div className="supplier-view-backdrop" role="presentation" onMouseDown={() => setSelectedSupplier(null)}>
          <section
            className="supplier-view-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="supplier-view-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="supplier-view-close"
              onClick={() => setSelectedSupplier(null)}
              aria-label="Close supplier details"
            >
              <X size={19} />
            </button>

            <p className="supplier-list-eyebrow">Supplier details</p>
            <h2 id="supplier-view-title">{selectedSupplier.supplier_name}</h2>
            <div className="supplier-view-grid">
              <div><span>Supplier number</span><strong>SUP-{String(selectedSupplier.supplier_id || "").padStart(4, "0")}</strong></div>
              <div><span>Status</span><strong>{selectedSupplier.status}</strong></div>
              <div><span>Phone</span><strong>{selectedSupplier.phone || "—"}</strong></div>
              <div><span>Email</span><strong>{selectedSupplier.email || "—"}</strong></div>
              <div><span>Currency</span><strong>{companyCurrency}</strong></div>
              <div>
                <span>Opening balance</span>
                <strong>
                  {formatSupplierCurrency(selectedSupplier.opening_balance || 0, companyCurrency)}
                  {" "}
                  {Number(selectedSupplier.opening_balance || 0) ? (selectedSupplier.opening_balance_side === "debit" ? "DR" : "CR") : ""}
                </strong>
              </div>
              <div><span>Opening date</span><strong>{selectedSupplier.opening_balance_date ? new Date(selectedSupplier.opening_balance_date).toLocaleDateString() : "—"}</strong></div>
              <div><span>Opening reference</span><strong>{selectedSupplier.opening_balance_reference || "—"}</strong></div>
              <div><span>Credit limit</span><strong>{formatSupplierCurrency(selectedSupplier.credit_limit || 0, companyCurrency)}</strong></div>
              <div><span>Company</span><strong>{selectedSupplier.company_id?.company_name || "—"}</strong></div>
            </div>
            <div className="supplier-view-note">
              Opening balance and credit limit do not post the Chart of Accounts. Opening balance is the supplier subledger allocation; credit limit is a purchase-control setting.
            </div>
          </section>
        </div>
      )}
    </main>
  );
}